import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, Archive, ArrowLeft, BarChart3, Bell, BellOff, CalendarDays, CheckCircle2, Database, Download,
  FileText, Flag, Hash, House, LogOut, Menu, MessageCircleMore, ImagePlus, MoreHorizontal, Paperclip, Plus,
  RefreshCw, Search, Send, Settings, Share2, ShieldCheck, Smile, Smartphone, Trash2, UserPlus, Users, X,
} from "lucide-react";

import { apiRequest, uploadAttachment } from "./api.js";
import AuthPanel from "./AuthPanel.jsx";
import Home from "./Home.jsx";
import Modal from "./Modal.jsx";

const roomColors = ["#2563eb", "#7c3aed", "#f59e0b", "#0f766e", "#db2777"];
const emojis = ["😀", "😊", "👍", "🙏", "🎉", "❤️", "✨", "😂", "👏", "✅"];
const fastPollWindowMs = 2 * 60 * 1000;
const conversationIdleMs = 5 * 60 * 1000;
const avatarOptions = [
  { id: "preset:boy-neutral", label: "남학생 평범" },
  { id: "preset:boy-smile", label: "남학생 웃음" },
  { id: "preset:boy-angry", label: "남학생 화남" },
  { id: "preset:boy-love", label: "남학생 사랑스러움" },
  { id: "preset:boy-surprised", label: "남학생 당황" },
  { id: "preset:girl-neutral", label: "여학생 평범" },
  { id: "preset:girl-smile", label: "여학생 웃음" },
  { id: "preset:girl-angry", label: "여학생 화남" },
  { id: "preset:girl-love", label: "여학생 사랑스러움" },
  { id: "preset:girl-surprised", label: "여학생 당황" },
];
const chatFontSizes = [
  { id: "xs", label: "매우 작게", scale: 0.84 },
  { id: "sm", label: "작게", scale: 0.92 },
  { id: "md", label: "보통", scale: 1 },
  { id: "lg", label: "크게", scale: 1.14 },
  { id: "xl", label: "매우 크게", scale: 1.3 },
];

function base64UrlToUint8Array(value) {
  const normalized = String(value).replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

async function detachPushSubscription() {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return;
  try {
    await apiRequest("/api/push/subscriptions", {
      method: "DELETE",
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
  } finally {
    await subscription.unsubscribe().catch(() => false);
  }
}

function SchoolChatMark() {
  return (
    <svg className="school-chat-mark" viewBox="0 0 32 32" aria-hidden="true">
      <path className="school-chat-mark__bubble" d="M8.4 5.5h15.2a4 4 0 0 1 4 4v8.4a4 4 0 0 1-4 4h-8.2L7 27l2.1-6.1a4 4 0 0 1-4.7-4V9.5a4 4 0 0 1 4-4Z" />
      <circle className="school-chat-mark__seal" cx="16" cy="14" r="5.4" />
      <text x="16" y="17.1" textAnchor="middle">고</text>
    </svg>
  );
}

function formatTime(value) {
  if (!value) return "";
  const normalized = String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ko-KR", { hour: "numeric", minute: "2-digit" }).format(date);
}

function parseTimestamp(value) {
  if (!value) return null;
  const normalized = String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateKey(value) {
  const date = parseTimestamp(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(value) {
  const date = parseTimestamp(value);
  if (!date) return "날짜 미상";
  const today = new Date();
  const todayKey = dateKey(today.toISOString());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (dateKey(value) === todayKey) return "오늘";
  if (dateKey(value) === dateKey(yesterday.toISOString())) return "어제";
  return new Intl.DateTimeFormat("ko-KR", { month: "long", day: "numeric", weekday: "short" }).format(date);
}

function formatRoomTime(value) {
  const date = parseTimestamp(value);
  if (!date) return "";
  return dateKey(value) === dateKey(new Date().toISOString())
    ? formatTime(value)
    : new Intl.DateTimeFormat("ko-KR", { month: "numeric", day: "numeric" }).format(date);
}

function personMeta(person) {
  if (person.identityProvider !== "namhae") return `@${person.loginId}`;
  const role = person.schoolRole === "teacher" ? "교사" : person.schoolRole === "admin" ? "관리자" : "학생";
  const classLabel = person.schoolRole === "student" && person.grade && person.classNumber
    ? `${person.grade}학년 ${person.classNumber}반${person.studentNumber ? ` ${person.studentNumber}번` : ""} · `
    : "";
  const loginLabel = ["teacher", "admin"].includes(person.schoolRole) && person.schoolLoginId
    ? ` · 아이디 ${person.schoolLoginId}`
    : "";
  return `${classLabel}${role}${loginLabel} · ${person.activated ? "남톡 이용 중" : "첫 접속 전"}`;
}

function avatarLabel(value) {
  const characters = Array.from(String(value || "").trim().replace(/\s+/g, ""));
  return characters.slice(-2).join("") || "?";
}

function AvatarPreset({ value }) {
  const [kind, expression = "neutral"] = String(value || "").replace("preset:", "").split("-");
  const girl = kind === "girl";
  return <svg className="avatar-preset" viewBox="0 0 64 64" aria-hidden="true"><rect width="64" height="64" rx="18" fill={girl ? "#fee2e2" : "#dbeafe"} />{girl && <path d="M13 34c0-18 8-27 19-27s19 9 19 27v20H13Z" fill="#293755" />}<circle cx="32" cy="33" r="18" fill="#f4c9a1" /><path d={girl ? "M15 29C17 14 23 9 32 9s15 5 17 20c-5-7-10-10-17-10S20 22 15 29Z" : "M15 27C17 13 24 8 34 9c8 1 13 6 15 15-7-1-12-4-17-9-4 6-10 10-17 12Z"} fill="#293755" />{expression === "love" ? <><text x="21" y="38" fontSize="12" textAnchor="middle" fill="#e11d48">♥</text><text x="43" y="38" fontSize="12" textAnchor="middle" fill="#e11d48">♥</text><path d="M25 43q7 7 14 0" fill="none" stroke="#a33a4b" strokeWidth="2.4" strokeLinecap="round" /></> : <><g fill="none" stroke="#172554" strokeWidth="2.5" strokeLinecap="round">{expression === "angry" ? <><path d="m19 30 7 2" /><path d="m45 30-7 2" /></> : null}<path d="M23 34h.1M41 34h.1" /></g>{expression === "smile" ? <path d="M24 41q8 8 16 0" fill="none" stroke="#9f3f43" strokeWidth="2.5" strokeLinecap="round" /> : expression === "angry" ? <path d="M25 46q7-6 14 0" fill="none" stroke="#9f3f43" strokeWidth="2.5" strokeLinecap="round" /> : expression === "surprised" ? <circle cx="32" cy="44" r="3.6" fill="#9f3f43" /> : <path d="M27 43h10" stroke="#9f3f43" strokeWidth="2.5" strokeLinecap="round" />}</>}{girl && <><circle cx="14" cy="39" r="3" fill="#facc15" /><circle cx="50" cy="39" r="3" fill="#facc15" /></>}</svg>;
}

function ProfileAvatar({ userId, avatarUrl, name, className, style }) {
  const classes = `${className || ""} profile-avatar-shell`.trim();
  if (String(avatarUrl || "").startsWith("preset:")) return <span className={classes} style={style}><AvatarPreset value={avatarUrl} /></span>;
  if (String(avatarUrl || "").startsWith("custom:") && userId) {
    const version = String(avatarUrl).split("/").at(-1);
    return <span className={classes} style={style}><img src={`/api/users/${encodeURIComponent(userId)}/avatar?v=${encodeURIComponent(version)}`} alt="" /></span>;
  }
  return <span className={classes} style={style}>{avatarLabel(name)}</span>;
}

function SwipeableConversation({ room, active, color, user, open, onOpen, onSelect, onToggleMute, onHide }) {
  const gestureRef = useRef(null);
  const [dragOffset, setDragOffset] = useState(0);
  const actionWidth = room.kind === "self" ? 58 : 116;
  const restingOffset = open ? -actionWidth : 0;

  useEffect(() => { if (!gestureRef.current) setDragOffset(restingOffset); }, [restingOffset]);

  function startGesture(event) {
    if (event.pointerType !== "touch") return;
    gestureRef.current = { x: event.clientX, y: event.clientY, offset: restingOffset, currentOffset: restingOffset, horizontal: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function moveGesture(event) {
    const gesture = gestureRef.current;
    if (!gesture || event.pointerType !== "touch") return;
    const deltaX = event.clientX - gesture.x;
    const deltaY = event.clientY - gesture.y;
    if (!gesture.horizontal && Math.abs(deltaX) < 7 && Math.abs(deltaY) < 7) return;
    if (!gesture.horizontal && Math.abs(deltaY) >= Math.abs(deltaX)) return;
    gesture.horizontal = true;
    gesture.currentOffset = Math.max(-actionWidth, Math.min(0, gesture.offset + deltaX));
    setDragOffset(gesture.currentOffset);
  }

  function finishGesture() {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture?.horizontal) return;
    const shouldOpen = gesture.currentOffset < -actionWidth * .38;
    setDragOffset(shouldOpen ? -actionWidth : 0);
    onOpen(shouldOpen ? room.id : "");
  }

  return <div className={`conversation-swipe-row ${open ? "conversation-swipe-row--open" : ""}`}>
    <div className="conversation-swipe-actions" aria-hidden={!open}>
      <button type="button" tabIndex={open ? 0 : -1} className="conversation-swipe-action conversation-swipe-action--mute" onClick={() => onToggleMute(room)} aria-label={room.muted ? `${room.name} 알림 켜기` : `${room.name} 알림 끄기`}>{room.muted ? <Bell size={17} /> : <BellOff size={17} />}<span>{room.muted ? "알림 켜기" : "알림 끄기"}</span></button>
      {room.kind !== "self" && <button type="button" tabIndex={open ? 0 : -1} className="conversation-swipe-action conversation-swipe-action--hide" onClick={() => onHide(room)} aria-label={`${room.name} 목록에서 삭제`}><Trash2 size={17} /><span>삭제</span></button>}
    </div>
    <button type="button" className={`conversation-card ${active ? "conversation-card--active" : ""}`} style={{ transform: `translateX(${dragOffset}px)` }} onPointerDown={startGesture} onPointerMove={moveGesture} onPointerUp={finishGesture} onPointerCancel={finishGesture} onClick={() => { if (open || dragOffset) { setDragOffset(0); onOpen(""); return; } onSelect(room.id); }}>
      <ProfileAvatar className="conversation-avatar" style={{ background: color }} userId={room.avatarUserId} avatarUrl={room.avatarUrl} name={room.kind === "self" ? user.displayName : room.name} />
      <span className="conversation-copy"><span className="conversation-title"><strong>{room.name}</strong><small>{formatRoomTime(room.latestAt)}</small></span><span className="conversation-preview">{room.preview || "첫 메시지를 보내보세요."}</span></span>
      {Number(room.unreadCount) > 0 && <span className="unread-badge" aria-label={`읽지 않은 메시지 ${room.unreadCount}개`}>{Number(room.unreadCount) > 99 ? "99+" : room.unreadCount}</span>}
    </button>
  </div>;
}

function readableBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

function isServiceAdmin(user) {
  return user?.schoolRole === "admin" || user?.serviceAdmin === true;
}

function isAtOrBeforeReadCursor(message, cursor) {
  if (!message || !cursor?.createdAt) return false;
  if (message.createdAt !== cursor.createdAt) return message.createdAt < cursor.createdAt;
  return message.id <= cursor.id;
}

function isNearMessageBottom(scroller) {
  if (!scroller) return true;
  return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= 72;
}

function App() {
  const [nativeEntry] = useState(() => {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
    const query = new URLSearchParams(window.location.search);
    return fragment.has("namhae") || query.get("open") === "chat";
  });
  const [screen, setScreen] = useState(() => nativeEntry ? "chat" : "home");
  const [backend, setBackend] = useState({ checking: true, database: "unconfigured", storage: "unconfigured" });
  const [authChecking, setAuthChecking] = useState(true);
  const [integrationError, setIntegrationError] = useState("");
  const [user, setUser] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState([]);
  const [peerReadThrough, setPeerReadThrough] = useState(null);
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState("");
  const [recallMenuMessageId, setRecallMenuMessageId] = useState("");
  const [powerSaving, setPowerSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null);
  const [viewedProfile, setViewedProfile] = useState(null);
  const [reportingMessage, setReportingMessage] = useState(null);
  const [swipedRoomId, setSwipedRoomId] = useState("");
  const [installPrompt, setInstallPrompt] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [chatFontSize, setChatFontSize] = useState(() => {
    const stored = window.localStorage.getItem("namtalk:chat-font-size");
    return chatFontSizes.some((option) => option.id === stored) ? stored : "md";
  });
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const messageScrollRef = useRef(null);
  const stickToMessageBottomRef = useRef(true);
  const restoreComposerFocusRef = useRef(false);
  const activeRoomIdRef = useRef("");
  const messageRequestIdRef = useRef(0);
  const messageCursorRef = useRef(null);
  const lastReadMessageIdRef = useRef("");
  const lastConversationActivityRef = useRef(Date.now());
  const nativeRoomListPresentedRef = useRef(false);
  const pinchRef = useRef(null);

  const activeRoom = rooms.find((room) => room.id === activeId) ?? null;
  const filteredRooms = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase("ko");
    return keyword ? rooms.filter((room) => `${room.name} ${room.preview || ""}`.toLocaleLowerCase("ko").includes(keyword)) : rooms;
  }, [query, rooms]);
  const chatFontScale = chatFontSizes.find((option) => option.id === chatFontSize)?.scale || 1;
  const latestReadMineId = useMemo(() => {
    if (activeRoom?.kind !== "direct" || !peerReadThrough) return "";
    return messages.reduce((latestId, message) => (
      message.authorId === user?.id && isAtOrBeforeReadCursor(message, peerReadThrough) ? message.id : latestId
    ), "");
  }, [activeRoom?.kind, messages, peerReadThrough, user?.id]);

  const changeChatFontSize = useCallback((value) => {
    if (!chatFontSizes.some((option) => option.id === value)) return;
    setChatFontSize(value);
    window.localStorage.setItem("namtalk:chat-font-size", value);
  }, []);

  const startMessagePinch = useCallback((event) => {
    if (event.touches.length !== 2) return;
    const [first, second] = event.touches;
    pinchRef.current = {
      distance: Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY),
      index: Math.max(0, chatFontSizes.findIndex((option) => option.id === chatFontSize)),
      appliedIndex: Math.max(0, chatFontSizes.findIndex((option) => option.id === chatFontSize)),
    };
  }, [chatFontSize]);

  const moveMessagePinch = useCallback((event) => {
    if (event.touches.length !== 2 || !pinchRef.current) return;
    event.preventDefault();
    const [first, second] = event.touches;
    const distance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
    const ratio = distance / Math.max(1, pinchRef.current.distance);
    const steps = Math.round(Math.log(ratio) / Math.log(1.18));
    const nextIndex = Math.max(0, Math.min(chatFontSizes.length - 1, pinchRef.current.index + steps));
    if (nextIndex === pinchRef.current.appliedIndex) return;
    pinchRef.current.appliedIndex = nextIndex;
    changeChatFontSize(chatFontSizes[nextIndex].id);
  }, [changeChatFontSize]);

  const endMessagePinch = useCallback((event) => {
    if (event.touches.length < 2) pinchRef.current = null;
  }, []);

  const markConversationActive = useCallback(() => {
    lastConversationActivityRef.current = Date.now();
    setPowerSaving(false);
  }, []);

  const wakeConversation = useCallback(() => {
    markConversationActive();
    window.dispatchEvent(new Event("namtalk:wake"));
  }, [markConversationActive]);

  const dismissComposerKeyboard = useCallback((event) => {
    const target = event.target;
    if (target instanceof Element && target.closest("button, a, input, textarea, select, [role='button']")) return;
    if (document.activeElement === messageInputRef.current) messageInputRef.current.blur();
  }, []);

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
    const namhaeToken = fragment.get("namhae");
    if (namhaeToken) window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    Promise.all([
      apiRequest("/api/health").catch(() => ({ database: "unconfigured", storage: "unconfigured" })),
      namhaeToken
        ? apiRequest("/api/auth/namhae", { method: "POST", body: JSON.stringify({ token: namhaeToken }) })
          .then((payload) => ({ authenticated: true, user: payload.user }))
          .catch((error) => { setIntegrationError(error.message); return { authenticated: false }; })
        : apiRequest("/api/auth/session").catch(() => ({ authenticated: false })),
    ]).then(([health, session]) => {
      setBackend({ checking: false, database: health.database, storage: health.storage });
      setUser(session.authenticated ? session.user : null);
      if (namhaeToken) setScreen("chat");
      setAuthChecking(false);
    });
  }, []);

  useEffect(() => {
    const rememberInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const clearInstallPrompt = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", rememberInstallPrompt);
    window.addEventListener("appinstalled", clearInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", rememberInstallPrompt);
      window.removeEventListener("appinstalled", clearInstallPrompt);
    };
  }, []);

  const loadRooms = useCallback(async () => {
    if (!user) return;
    try {
      const payload = await apiRequest("/api/rooms");
      setRooms(payload.rooms);
      setActiveId((current) => {
        if (payload.rooms.some((room) => room.id === current)) return current;
        // The native NamhaeTime handoff must open the conversation list, not
        // silently restore the most recently active room. On released native
        // builds a delayed handoff can otherwise look as if NamTalk captured
        // every tab because the first (latest) room immediately fills the UI.
        return nativeEntry ? "" : payload.rooms[0]?.id || "";
      });
      if (nativeEntry && payload.rooms.length && !nativeRoomListPresentedRef.current) {
        nativeRoomListPresentedRef.current = true;
        setSidebarOpen(true);
      }
      return true;
    } catch (error) {
      if (error.status === 401) setUser(null);
      else setNotice(error.message);
      return false;
    }
  }, [nativeEntry, user]);

  useEffect(() => {
    if (!user) nativeRoomListPresentedRef.current = false;
  }, [user]);

  const acknowledgeRead = useCallback((roomId, messageId) => {
    if (!roomId || !messageId || lastReadMessageIdRef.current === messageId) return;
    lastReadMessageIdRef.current = messageId;
    apiRequest(`/api/rooms/${roomId}/read`, { method: "POST", body: JSON.stringify({ messageId }) })
      .then(() => setRooms((current) => current.map((room) => room.id === roomId ? { ...room, unreadCount: 0 } : room)))
      .catch(() => { if (lastReadMessageIdRef.current === messageId) lastReadMessageIdRef.current = ""; });
  }, []);

  const loadMessages = useCallback(async ({ quiet = false, incremental = false } = {}) => {
    if (!activeId || !user) return;
    const roomId = activeId;
    const requestId = ++messageRequestIdRef.current;
    const shouldAcknowledge = !incremental || isNearMessageBottom(messageScrollRef.current);
    stickToMessageBottomRef.current = shouldAcknowledge;
    if (!quiet) setLoadingMessages(true);
    try {
      const query = new URLSearchParams({ limit: "80" });
      const cursor = incremental ? messageCursorRef.current : null;
      if (cursor) {
        query.set("afterCreatedAt", cursor.createdAt);
        query.set("afterId", cursor.id);
      }
      const payload = await apiRequest(`/api/rooms/${roomId}/messages?${query}`);
      if (activeRoomIdRef.current !== roomId || messageRequestIdRef.current !== requestId) return true;
      setPeerReadThrough(payload.peerReadThrough ?? null);
      setMessages((current) => incremental
        ? [...current, ...payload.messages.filter((message) => !current.some((item) => item.id === message.id))]
        : payload.messages);
      const lastMessage = payload.messages.at(-1);
      const previousCursor = messageCursorRef.current;
      if (!incremental) messageCursorRef.current = lastMessage ? { id: lastMessage.id, createdAt: lastMessage.createdAt } : null;
      else if (lastMessage) messageCursorRef.current = { id: lastMessage.id, createdAt: lastMessage.createdAt };
      if (lastMessage && previousCursor && previousCursor.id !== lastMessage.id) markConversationActive();
      if (lastMessage && shouldAcknowledge && document.visibilityState === "visible") acknowledgeRead(roomId, lastMessage.id);
      return true;
    } catch (error) {
      setNotice(error.message);
      return false;
    } finally {
      if (!quiet) setLoadingMessages(false);
    }
  }, [acknowledgeRead, activeId, markConversationActive, user]);

  useEffect(() => { if (screen === "chat" && user) loadRooms(); }, [loadRooms, screen, user]);
  useEffect(() => {
    if (!activeId || screen !== "chat" || !user) return undefined;
    activeRoomIdRef.current = activeId;
    messageRequestIdRef.current += 1;
    messageCursorRef.current = null;
    lastReadMessageIdRef.current = "";
    stickToMessageBottomRef.current = true;
    setPeerReadThrough(null);
    markConversationActive();
    setMessages([]);
    let cancelled = false;
    let messageTimer;
    let retryDelay = 3_000;
    let syncInFlight = false;
    const canSync = () => document.visibilityState === "visible" && navigator.onLine;
    const activityPollDelay = () => {
      const activeFor = Date.now() - lastConversationActivityRef.current;
      if (activeFor < fastPollWindowMs) return 3_000;
      return 8_000;
    };
    const syncMessages = async ({ initial = false, refreshRooms = false } = {}) => {
      if (!canSync() || syncInFlight) return null;
      syncInFlight = true;
      try {
        const [messagesSucceeded] = await Promise.all([
          loadMessages({ quiet: !initial, incremental: !initial }),
          refreshRooms ? loadRooms() : Promise.resolve(true),
        ]);
        return messagesSucceeded;
      } finally {
        syncInFlight = false;
      }
    };
    const pollMessages = async () => {
      if (cancelled) return;
      const idle = Date.now() - lastConversationActivityRef.current >= conversationIdleMs;
      setPowerSaving(idle);
      if (canSync() && !idle) {
        const succeeded = await syncMessages();
        if (succeeded === true) retryDelay = activityPollDelay();
        else if (succeeded === false) retryDelay = Math.min(retryDelay * 2, 60_000);
      }
      if (!cancelled) messageTimer = window.setTimeout(pollMessages, idle ? 60_000 : retryDelay);
    };
    const wakeAndSync = () => {
      if (document.visibilityState !== "visible") return;
      markConversationActive();
      window.clearTimeout(messageTimer);
      void syncMessages({ refreshRooms: true });
      messageTimer = window.setTimeout(pollMessages, 3_000);
    };
    void syncMessages({ initial: true });
    messageTimer = window.setTimeout(pollMessages, retryDelay);
    document.addEventListener("visibilitychange", wakeAndSync);
    window.addEventListener("focus", wakeAndSync);
    window.addEventListener("online", wakeAndSync);
    window.addEventListener("namtalk:wake", wakeAndSync);
    return () => {
      cancelled = true;
      window.clearTimeout(messageTimer);
      document.removeEventListener("visibilitychange", wakeAndSync);
      window.removeEventListener("focus", wakeAndSync);
      window.removeEventListener("online", wakeAndSync);
      window.removeEventListener("namtalk:wake", wakeAndSync);
    };
  }, [activeId, loadMessages, loadRooms, markConversationActive, screen, user]);
  useEffect(() => {
    const scroller = messageScrollRef.current;
    if (stickToMessageBottomRef.current) scroller?.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
  }, [messages]);
  useEffect(() => { setRecallMenuMessageId(""); }, [activeId]);
  useEffect(() => {
    if (screen !== "chat" || !window.visualViewport) return undefined;
    let frame;
    const keepLatestMessageVisible = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const scroller = messageScrollRef.current;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    };
    window.visualViewport.addEventListener("resize", keepLatestMessageVisible);
    return () => {
      cancelAnimationFrame(frame);
      window.visualViewport.removeEventListener("resize", keepLatestMessageVisible);
    };
  }, [screen]);
  useEffect(() => { if (!notice) return undefined; const timer = setTimeout(() => setNotice(""), 3500); return () => clearTimeout(timer); }, [notice]);
  useEffect(() => {
    if (sending || !restoreComposerFocusRef.current) return undefined;
    restoreComposerFocusRef.current = false;
    const frame = requestAnimationFrame(() => messageInputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [sending]);

  async function sendMessage(event, attachmentId = null) {
    event?.preventDefault?.();
    if (!activeId || sending) return;
    const body = draft.trim();
    if (!body && !attachmentId) return;
    // Keep the composer focused inside the user gesture. iOS does not reliably
    // reopen its keyboard after an asynchronous request finishes.
    messageInputRef.current?.focus({ preventScroll: true });
    restoreComposerFocusRef.current = true;
    if (body) setDraft("");
    setSending(true);
    try {
      const payload = await apiRequest(`/api/rooms/${activeId}/messages`, { method: "POST", body: JSON.stringify({ body, attachmentId }) });
      setEmojiOpen(false);
      stickToMessageBottomRef.current = true;
      setMessages((current) => current.some((message) => message.id === payload.message.id) ? current : [...current, payload.message]);
      messageCursorRef.current = { id: payload.message.id, createdAt: payload.message.createdAt };
      lastReadMessageIdRef.current = payload.message.id;
      markConversationActive();
      setRooms((current) => current
        .map((room) => room.id === activeId ? { ...room, preview: payload.message.body, latestAt: payload.message.createdAt, unreadCount: 0 } : room)
        .sort((left, right) => String(right.latestAt || right.createdAt).localeCompare(String(left.latestAt || left.createdAt))));
    } catch (error) {
      if (body) setDraft((current) => current.trim() ? `${body}\n${current}` : body);
      setNotice(error.message);
    } finally {
      setSending(false);
    }
  }

  async function handleFile(file) {
    if (!file || !activeId) return;
    setSending(true);
    setNotice("첨부파일을 올리고 있습니다…");
    try {
      const payload = await uploadAttachment(activeId, file);
      setSending(false);
      await sendMessage(null, payload.attachment.id);
      setNotice("첨부파일을 보냈습니다.");
    } catch (error) {
      setNotice(error.message);
      setSending(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function recallMessage(message) {
    if (!activeId || deletingMessageId || message.authorId !== user.id) return;
    if (!window.confirm("이 메시지를 완전히 회수할까요? 상대방 대화에서도 바로 사라집니다.")) return;
    setDeletingMessageId(message.id);
    try {
      await apiRequest(`/api/rooms/${activeId}/messages/${message.id}`, { method: "DELETE" });
      setMessages((current) => current.filter((item) => item.id !== message.id));
      setRecallMenuMessageId("");
      await loadRooms();
    } catch (error) {
      setNotice(error.message);
    } finally {
      setDeletingMessageId("");
    }
  }

  async function createRoom(name) {
    const payload = await apiRequest("/api/rooms", { method: "POST", body: JSON.stringify({ name }) });
    await loadRooms();
    setActiveId(payload.room.id);
    setModal(null);
  }

  async function startDirect(userId) {
    const payload = await apiRequest("/api/rooms", { method: "POST", body: JSON.stringify({ kind: "direct", userId }) });
    await loadRooms();
    setActiveId(payload.room.id);
    setModal(null);
  }

  async function toggleMute() {
    if (!activeRoom) return;
    const muted = !Boolean(activeRoom.muted);
    await apiRequest(`/api/rooms/${activeRoom.id}/settings`, { method: "PATCH", body: JSON.stringify({ muted }) });
    setRooms((current) => current.map((room) => room.id === activeRoom.id ? { ...room, muted: muted ? 1 : 0 } : room));
    setNotice(muted ? "이 대화방 알림을 껐습니다." : "이 대화방 알림을 켰습니다.");
  }

  async function toggleRoomMute(room) {
    const muted = !Boolean(room.muted);
    try {
      await apiRequest(`/api/rooms/${room.id}/settings`, { method: "PATCH", body: JSON.stringify({ muted }) });
      setRooms((current) => current.map((item) => item.id === room.id ? { ...item, muted: muted ? 1 : 0 } : item));
      setSwipedRoomId("");
      setNotice(muted ? "이 대화의 알림을 껐습니다." : "이 대화의 알림을 켰습니다.");
    } catch (error) {
      setNotice(error.message);
    }
  }

  async function hideRoom(room) {
    if (!window.confirm(`'${room.name}' 대화를 목록에서 숨길까요? 메시지는 삭제되지 않으며 새 메시지가 오면 다시 표시됩니다.`)) return;
    try {
      await apiRequest(`/api/rooms/${room.id}/settings`, { method: "DELETE" });
      const remaining = rooms.filter((item) => item.id !== room.id);
      setRooms(remaining);
      if (activeId === room.id) setActiveId(remaining[0]?.id || "");
      setSwipedRoomId("");
      setNotice("대화를 목록에서 숨겼습니다.");
    } catch (error) {
      setNotice(error.message);
    }
  }

  async function logout() {
    await detachPushSubscription().catch(() => {});
    await apiRequest("/api/auth/logout", { method: "POST" });
    setUser(null); setRooms([]); setMessages([]); setActiveId(""); setModal(null); setScreen("home");
  }

  if (authChecking) return <main className="auth-page"><p>로그인 상태를 확인하고 있습니다…</p></main>;
  if (screen === "home") return <Home backend={backend} onEnter={() => setScreen("chat")} />;
  if (!user) return <AuthPanel initialError={integrationError} onAuthenticated={(nextUser) => { setUser(nextUser); setIntegrationError(""); setScreen("chat"); }} onBack={() => setScreen("home")} />;
  if (screen === "admin" && isServiceAdmin(user)) return <AdminDashboard user={user} onBack={() => setScreen("chat")} />;

  const backendReady = backend.database === "ready" && backend.storage === "ready";

  return (
    <main className="app-shell" style={{ "--chat-font-scale": chatFontScale }}>
      <aside className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`} aria-label="대화 목록">
        <div className="brand-row">
          <button className="brand-mark" type="button" onClick={() => setScreen("home")} aria-label="홈으로 이동"><SchoolChatMark /></button>
          <div><strong>NamTalk</strong><span>{backendReady ? "학교 대화 연결됨" : "연결 확인 중"}</span></div>
          <button className="icon-button mobile-close" type="button" onClick={() => setSidebarOpen(false)} aria-label="대화 목록 닫기"><X size={20} /></button>
        </div>
        <button className="new-chat-button" type="button" onClick={() => setModal("new-conversation")}><Plus size={18} /> 새 메시지</button>
        <label className="search-box"><Search size={17} aria-hidden="true" /><span className="sr-only">대화 검색</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="대화 검색" /></label>
        <div className="section-label"><span>대화 {filteredRooms.length}</span></div>
        <nav className="conversation-list">
          {filteredRooms.map((room, index) => <SwipeableConversation key={room.id} room={room} active={activeId === room.id} color={roomColors[index % roomColors.length]} user={user} open={swipedRoomId === room.id} onOpen={setSwipedRoomId} onSelect={(roomId) => { setActiveId(roomId); setRooms((current) => current.map((item) => item.id === roomId ? { ...item, unreadCount: 0 } : item)); setSidebarOpen(false); }} onToggleMute={toggleRoomMute} onHide={hideRoom} />)}
          {!filteredRooms.length && <p className="empty-sidebar">{query ? "검색 결과가 없습니다." : "새 대화를 만들어보세요."}</p>}
        </nav>
        <div className="sidebar-footer">
          <ProfileAvatar className="profile-avatar" userId={user.id} avatarUrl={user.avatarUrl} name={user.displayName} />
          <div><strong>{user.displayName}</strong><span className="connection-state"><i className={`status-dot status-dot--${backendReady ? "ready" : "checking"}`} />{user.identityProvider === "namhae" ? "남해타임 연동" : `@${user.loginId}`}</span></div>
          <div className="sidebar-footer-actions">
            {isServiceAdmin(user) && <button className="icon-button admin-entry-button" type="button" onClick={() => setScreen("admin")} aria-label="관리자 페이지" title="관리자 페이지"><BarChart3 size={19} /></button>}
            <button className="icon-button" type="button" onClick={() => setModal("profile")} aria-label="설정"><Settings size={19} /></button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <button type="button" className="backdrop" onClick={() => setSidebarOpen(false)} aria-label="대화 목록 닫기" />}

      <section className="chat-panel" aria-label={activeRoom ? `${activeRoom.name} 대화방` : "대화방"}>
        <header className="chat-header">
          <button className="icon-button menu-button" type="button" onClick={() => setSidebarOpen(true)} aria-label="대화 목록 열기"><Menu size={21} /></button>
          <div className="room-identity"><span className="room-icon">{activeRoom?.kind === "direct" ? <MessageCircleMore size={18} /> : <Hash size={18} />}</span><div><strong>{activeRoom?.name || "대화를 선택하세요"}</strong><span>{activeRoom ? `멤버 ${activeRoom.memberCount || 1}명 · ${activeRoom.kind === "self" ? "나만의 메모 공간" : activeRoom.kind === "direct" ? "1:1 대화" : "그룹 대화"}` : "왼쪽에서 대화를 선택하세요"}</span></div></div>
          <div className="header-actions">
            <a className="namhaetime-link" href="https://namhae.pages.dev/" target="_blank" rel="noreferrer" aria-label="남해타임 새 탭에서 열기"><CalendarDays size={17} /><span>남해타임</span></a>
            <button className="icon-button home-button" type="button" onClick={() => setScreen("home")} aria-label="홈으로 이동"><House size={19} /></button>
            <button className="push-quick-button" type="button" onClick={() => setModal("push-notifications")} aria-label="푸시 알림 켜기 및 설치 안내"><Bell size={18} /><span>푸시 알림</span></button>
            <button className={`icon-button ${activeRoom?.muted ? "icon-button--active" : ""}`} type="button" onClick={toggleMute} disabled={!activeRoom} aria-label={activeRoom?.muted ? "알림 켜기" : "알림 끄기"} aria-pressed={Boolean(activeRoom?.muted)}>{activeRoom?.muted ? <BellOff size={19} /> : <Bell size={19} />}</button>
            <button className="member-button" type="button" onClick={() => setModal("members")} disabled={!activeRoom}><Users size={18} /><span>{activeRoom?.memberCount || 0}</span></button>
            <button className="icon-button" type="button" onClick={() => setModal("room-settings")} disabled={!activeRoom} aria-label="대화방 설정"><MoreHorizontal size={20} /></button>
          </div>
        </header>

        <div className="message-scroll" ref={messageScrollRef} onClick={dismissComposerKeyboard} onTouchStart={startMessagePinch} onTouchMove={moveMessagePinch} onTouchEnd={endMessagePinch} onTouchCancel={endMessagePinch} onScroll={(event) => { const atBottom = isNearMessageBottom(event.currentTarget); stickToMessageBottomRef.current = atBottom; if (atBottom) acknowledgeRead(activeId, messages.at(-1)?.id); }}>
          {!activeRoom ? <div className="room-welcome"><span><MessageCircleMore size={24} /></span><h1>대화를 시작해보세요</h1><p>새 메시지에서 사람을 찾아 바로 이야기할 수 있습니다.</p></div> : (
            <><div className="room-welcome"><span>{activeRoom.kind === "direct" ? <MessageCircleMore size={24} /> : <Hash size={24} />}</span><h1>{activeRoom.name}</h1><p>{activeRoom.kind === "self" ? "생각과 자료를 나에게 보내 보관하세요." : activeRoom.kind === "direct" ? "두 사람만 참여하는 1:1 대화입니다." : "멤버들과 메시지와 파일을 안전하게 나눠보세요."}</p></div>
              <div className="messages">{messages.map((message, index) => {
                const mine = message.authorId === user.id;
                const previous = messages[index - 1];
                const currentAt = parseTimestamp(message.createdAt);
                const previousAt = parseTimestamp(previous?.createdAt);
                const compact = Boolean(previous && previous.authorId === message.authorId && dateKey(previous.createdAt) === dateKey(message.createdAt) && currentAt && previousAt && currentAt - previousAt < 5 * 60 * 1000);
                const showDate = !previous || dateKey(previous.createdAt) !== dateKey(message.createdAt);
                const avatar = <ProfileAvatar className={`message-avatar message-avatar--${mine ? "mine" : "other"}`} userId={message.authorId} avatarUrl={message.authorAvatar || (mine ? user.avatarUrl : null)} name={message.authorName} />;
                const recallMenuOpen = mine && recallMenuMessageId === message.id;
                return <Fragment key={message.id}>{showDate && <div className="day-divider"><span>{formatDateLabel(message.createdAt)}</span></div>}<article className={`message ${mine ? "message--mine" : ""} ${compact ? "message--compact" : ""}`}>{mine ? avatar : <button className="message-profile-button" type="button" onClick={() => setViewedProfile({ id: message.authorId, displayName: message.authorName, realName: message.authorRealName || null, avatarUrl: message.authorAvatar || null, bio: message.authorBio || "" })} aria-label={`${message.authorName} 프로필 보기`} title="프로필 보기">{avatar}</button>}<div className="message-content"><div className="message-meta"><strong>{mine ? "나" : message.authorName}</strong><time>{formatTime(message.createdAt)}</time></div><div className="message-bubble-row">{recallMenuOpen && <button className="message-recall-button" type="button" onClick={() => recallMessage(message)} disabled={Boolean(deletingMessageId)} aria-label="이 메시지 완전히 회수" title="메시지 회수"><Trash2 size={14} aria-hidden="true" /></button>}{mine ? <button className={`message-bubble message-bubble--selectable ${recallMenuOpen ? "message-bubble--selected" : ""}`} type="button" onClick={() => setRecallMenuMessageId((current) => current === message.id ? "" : message.id)} aria-expanded={recallMenuOpen} aria-label="보낸 메시지 작업 열기">{message.body}</button> : <><p className="message-bubble">{message.body}</p><button className="message-safety-button" type="button" onClick={() => setReportingMessage(message)} aria-label={`${message.authorName}의 메시지 신고 및 차단`} title="신고 또는 차단"><Flag size={13} /></button></>}</div>{message.attachments?.map((attachment) => <a className="attachment-card" href={`/api/attachments/${attachment.id}`} target="_blank" rel="noreferrer" key={attachment.id}><FileText size={18} /><span><strong>{attachment.originalName}</strong><small>{readableBytes(attachment.byteSize)}</small></span></a>)}{message.id === latestReadMineId && <span className="message-read-status">읽음</span>}</div></article></Fragment>;
              })}{loadingMessages && !messages.length && <p className="empty-messages">메시지를 불러오는 중입니다…</p>}{!loadingMessages && !messages.length && <p className="empty-messages">아직 메시지가 없습니다. 첫 인사를 남겨보세요.</p>}</div></>
          )}
        </div>

        <form className="composer" onSubmit={sendMessage}>
          <div className="composer-inner">
            <button className="icon-button" type="button" onClick={() => fileInputRef.current?.click()} disabled={!activeRoom || sending} aria-label="파일 첨부"><Paperclip size={20} /></button><input ref={fileInputRef} className="sr-only" type="file" onChange={(event) => handleFile(event.target.files?.[0])} />
            <label><span className="sr-only">메시지 입력</span><textarea ref={messageInputRef} value={draft} onFocus={() => { wakeConversation(); document.documentElement.dataset.composerFocused = "true"; }} onBlur={() => { delete document.documentElement.dataset.composerFocused; }} onChange={(event) => { if (powerSaving) wakeConversation(); else markConversationActive(); setDraft(event.target.value); }} disabled={!activeRoom} placeholder={activeRoom ? "메시지를 입력하세요" : "대화방을 선택하세요"} rows="1" onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); sendMessage(event); } }} /></label>
            <div className="emoji-wrap"><button className="icon-button emoji-button" type="button" onClick={() => setEmojiOpen((current) => !current)} disabled={!activeRoom} aria-label="이모지" aria-expanded={emojiOpen}><Smile size={20} /></button>{emojiOpen && <div className="emoji-popover">{emojis.map((emoji) => <button type="button" key={emoji} onClick={() => { setDraft((current) => `${current}${emoji}`); setEmojiOpen(false); }}>{emoji}</button>)}</div>}</div>
            <button className="send-button" type="submit" disabled={!activeRoom || !draft.trim() || sending} aria-label="메시지 보내기"><Send size={19} /></button>
          </div><p>{powerSaving ? <span className="power-saving-label">절전 대기 · 활동하거나 알림을 열면 새 메시지 확인</span> : "Enter로 보내기 · Shift + Enter로 줄바꿈 · 파일 최대 10MB"}</p>
        </form>
      </section>
      {notice && <div className="toast" role="status">{notice}</div>}
      {modal === "new-conversation" && <NewConversationModal onClose={() => setModal(null)} onCreateGroup={createRoom} onStartDirect={startDirect} />}
      {modal === "profile" && <ProfileModal user={user} chatFontSize={chatFontSize} onChatFontSizeChange={changeChatFontSize} onClose={() => setModal(null)} onSaved={setUser} onLogout={logout} />}
      {modal === "members" && activeRoom && <MembersModal room={activeRoom} currentUser={user} onClose={() => setModal(null)} onChanged={loadRooms} />}
      {modal === "room-settings" && activeRoom && <RoomSettingsModal room={activeRoom} onClose={() => setModal(null)} onChanged={async () => { await loadRooms(); setModal(null); }} />}
      {modal === "push-notifications" && <PushNotificationGuideModal installPrompt={installPrompt} onInstallPromptUsed={() => setInstallPrompt(null)} onClose={() => setModal(null)} />}
      {viewedProfile && <PublicProfileModal profile={viewedProfile} onClose={() => setViewedProfile(null)} />}
      {reportingMessage && activeRoom && <SafetyReportModal message={reportingMessage} room={activeRoom} onClose={() => setReportingMessage(null)} onComplete={(message) => { setReportingMessage(null); setNotice(message); }} />}
    </main>
  );
}

function SafetyReportModal({ message, room, onClose, onComplete }) {
  const [reason, setReason] = useState("harassment");
  const [note, setNote] = useState("");
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return <Modal title="메시지 신고" onClose={onClose}><form className="modal-form" onSubmit={async (event) => { event.preventDefault(); setSaving(true); setError(""); try { await apiRequest("/api/reports", { method: "POST", body: JSON.stringify({ roomId: room.id, messageId: message.id, reason, note }) }); if (alsoBlock) await apiRequest("/api/blocks", { method: "POST", body: JSON.stringify({ userId: message.authorId }) }); onComplete(alsoBlock ? "신고를 접수했고 사용자를 차단했습니다." : "신고를 접수했습니다."); } catch (requestError) { setError(requestError.message); setSaving(false); } }}><p className="modal-help">신고는 운영 검토를 위해 접수됩니다. 긴급한 위험은 학교 또는 긴급기관에 바로 알려주세요.</p><label><span>사유</span><select value={reason} onChange={(event) => setReason(event.target.value)}><option value="harassment">괴롭힘·혐오 표현</option><option value="abuse">욕설·모욕</option><option value="sexual">성적 내용</option><option value="violence">폭력·위협</option><option value="spam">스팸·광고</option><option value="other">기타</option></select></label><label><span>추가 설명 (선택)</span><textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength="500" placeholder="운영자가 확인할 내용을 적어주세요." /></label><label className="safety-check"><input type="checkbox" checked={alsoBlock} onChange={(event) => setAlsoBlock(event.target.checked)} /> 이 사용자를 차단하고 이후 메시지를 숨기기</label>{error && <p className="form-error">{error}</p>}<button className="modal-danger-outline" type="submit" disabled={saving}>{saving ? "접수 중…" : "신고 접수"}</button></form></Modal>;
}

function formatMetricBucket(value, bucket) {
  if (!value) return "";
  const date = new Date(bucket === "day" ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return bucket === "hour"
    ? new Intl.DateTimeFormat("ko-KR", { hour: "numeric" }).format(date)
    : new Intl.DateTimeFormat("ko-KR", { month: "numeric", day: "numeric" }).format(date);
}

function AdminDashboard({ user, onBack }) {
  const [range, setRange] = useState("24h");
  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportActionId, setReportActionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async ({ fresh = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const suffix = fresh ? `&fresh=${Date.now()}` : "";
      setMetrics(await apiRequest(`/api/admin/metrics?range=${range}${suffix}`, { cache: fresh ? "reload" : "default" }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const payload = await apiRequest("/api/admin/reports?status=open", { cache: "no-store" });
      setReports(payload.reports || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReportsLoading(false);
    }
  }, []);
  useEffect(() => { loadReports(); }, [loadReports]);
  const updateReport = useCallback(async (report, status, removeMessage = false) => {
    setReportActionId(report.id);
    setError("");
    try {
      if (removeMessage && report.roomId && report.messageId && !report.messageDeletedAt) {
        await apiRequest(`/api/rooms/${encodeURIComponent(report.roomId)}/messages/${encodeURIComponent(report.messageId)}`, { method: "DELETE" });
      }
      await apiRequest("/api/admin/reports", { method: "PATCH", body: JSON.stringify({ id: report.id, status }) });
      await loadReports();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReportActionId("");
    }
  }, [loadReports]);
  const maxMessages = Math.max(1, ...(metrics?.timeline || []).map((item) => item.messageCount));
  const rangeLabel = range === "24h" ? "최근 24시간" : range === "7d" ? "최근 7일" : "최근 30일";

  return <main className="admin-page">
    <header className="admin-header">
      <button className="admin-back-button" type="button" onClick={onBack} aria-label="남톡으로 돌아가기"><ArrowLeft size={18} /><span className="admin-back-wordmark official-wordmark official-wordmark--talk" aria-hidden="true"><span>남</span><span>톡</span></span></button>
      <div className="admin-title"><span><ShieldCheck size={21} /></span><div><strong>남톡 관리자</strong><small>내용을 보지 않는 활동량 모니터링</small></div></div>
      <div className="admin-user"><strong>{user.displayName}</strong><span>관리자</span></div>
    </header>
    <section className="admin-content">
      <div className="admin-overview-heading">
        <div><span className="admin-eyebrow">SERVICE ACTIVITY</span><h1>메시지 흐름만 확인합니다.</h1><p>메시지 내용과 대화 상대는 표시하지 않습니다. 활동량을 통해 D1 사용 부담의 변화를 살펴보세요.</p></div>
        <div className="admin-range-controls" role="group" aria-label="활동 조회 기간">
          {[{ id: "24h", label: "24시간" }, { id: "7d", label: "7일" }, { id: "30d", label: "30일" }].map((option) => <button type="button" key={option.id} className={range === option.id ? "active" : ""} onClick={() => setRange(option.id)}>{option.label}</button>)}
          <button className="admin-refresh-button" type="button" onClick={() => load({ fresh: true })} disabled={loading} aria-label="통계 새로고침"><RefreshCw size={16} className={loading ? "spin" : ""} /></button>
        </div>
      </div>
      {error && <div className="admin-error">{error}</div>}
      <div className="metric-grid" aria-busy={loading}>
        <article><span className="metric-icon metric-icon--yellow"><MessageCircleMore size={20} /></span><small>{rangeLabel} 메시지</small><strong>{metrics?.activity.messageCount?.toLocaleString("ko-KR") ?? "—"}</strong><em>전송 건수</em></article>
        <article><span className="metric-icon"><Activity size={20} /></span><small>활성 사용자</small><strong>{metrics?.activity.activeUsers?.toLocaleString("ko-KR") ?? "—"}</strong><em>메시지를 보낸 계정</em></article>
        <article><span className="metric-icon"><Hash size={20} /></span><small>활성 대화방</small><strong>{metrics?.activity.activeRooms?.toLocaleString("ko-KR") ?? "—"}</strong><em>메시지가 오간 방</em></article>
        <article><span className="metric-icon"><Paperclip size={20} /></span><small>첨부 메시지</small><strong>{metrics?.activity.attachmentMessages?.toLocaleString("ko-KR") ?? "—"}</strong><em>파일 포함 건수</em></article>
      </div>
      <div className="admin-detail-grid">
        <section className="admin-card activity-chart-card">
          <div className="admin-card-heading"><div><span>메시지 추이</span><strong>{rangeLabel}</strong></div><BarChart3 size={20} /></div>
          <div className="activity-chart">
            {(metrics?.timeline || []).length ? metrics.timeline.map((item) => <div className="activity-bar-column" key={item.bucket} title={`${formatMetricBucket(item.bucket, metrics.bucket)} · ${item.messageCount}건`}><span className="activity-value">{item.messageCount || ""}</span><i style={{ height: `${Math.max(item.messageCount ? 8 : 2, (item.messageCount / maxMessages) * 100)}%` }} /><small>{formatMetricBucket(item.bucket, metrics.bucket)}</small></div>) : <p className="admin-empty">선택한 기간에 전송된 메시지가 없습니다.</p>}
          </div>
        </section>
        <section className="admin-card">
          <div className="admin-card-heading"><div><span>대화 형태</span><strong>메시지 분포</strong></div><MessageCircleMore size={20} /></div>
          <div className="room-type-list">
            {[{ key: "direct", label: "1:1 대화" }, { key: "group", label: "그룹 대화" }, { key: "self", label: "나에게 보내기" }].map((item) => <div key={item.key}><span>{item.label}</span><strong>{Number(metrics?.roomTypes?.[item.key] || 0).toLocaleString("ko-KR")}건</strong></div>)}
          </div>
        </section>
        <section className="admin-card admin-storage-card">
          <div className="admin-card-heading"><div><span>현재 누적</span><strong>서비스 규모</strong></div><Database size={20} /></div>
          <div className="storage-stats"><div><span>사용자</span><strong>{metrics?.totals.users?.toLocaleString("ko-KR") ?? "—"}</strong></div><div><span>대화방</span><strong>{metrics?.totals.rooms?.toLocaleString("ko-KR") ?? "—"}</strong></div><div><span>메시지</span><strong>{metrics?.totals.messages?.toLocaleString("ko-KR") ?? "—"}</strong></div><div><span>첨부 용량</span><strong>{metrics ? readableBytes(metrics.totals.attachmentBytes) : "—"}</strong></div></div>
        </section>
        <section className="admin-card admin-guide-card">
          <div className="admin-card-heading"><div><span>부하 확인</span><strong>이렇게 판단하세요</strong></div><ShieldCheck size={20} /></div>
          <p>메시지 수와 활성 인원이 갑자기 늘면 Cloudflare에서 D1의 실제 <b>Rows read</b>와 <b>Rows written</b>을 함께 확인하세요. 이 화면은 원문을 읽지 않으며 자동 새로고침도 하지 않습니다.</p>
          <a href="https://dash.cloudflare.com/?to=/:account/workers-and-pages/d1" target="_blank" rel="noreferrer">Cloudflare D1 상세 지표 열기 <ArrowLeft size={15} /></a>
        </section>
      </div>
      <section className="admin-card report-review-card" aria-busy={reportsLoading}>
        <div className="admin-card-heading"><div><span>SAFETY REVIEW</span><strong>신고 검토</strong></div><Flag size={20} /></div>
        <p className="report-review-help">신고된 메시지만 운영자가 검토합니다. 판단 후 해결 처리하거나, 규정 위반 메시지는 즉시 삭제할 수 있습니다.</p>
        {reportsLoading ? <p className="admin-empty">신고를 불러오는 중입니다…</p> : !reports.length ? <p className="admin-empty">검토할 신고가 없습니다.</p> : <div className="report-review-list">{reports.map((report) => <article key={report.id}>
          <div className="report-review-meta"><strong>{report.reportedName || "알 수 없는 사용자"}</strong><span>{report.roomName || "대화방"} · {formatTime(report.createdAt)}</span></div>
          <p className="report-review-reason">신고 사유: {report.reason}{report.note ? ` · ${report.note}` : ""}</p>
          <blockquote>{report.messageDeletedAt ? "이미 삭제된 메시지입니다." : report.messageBody || "메시지 내용을 찾을 수 없습니다."}</blockquote>
          <div className="report-review-actions"><button type="button" onClick={() => updateReport(report, "reviewed")} disabled={Boolean(reportActionId)}>검토 중</button><button type="button" onClick={() => updateReport(report, "resolved")} disabled={Boolean(reportActionId)}>해결 처리</button>{!report.messageDeletedAt && <button className="report-remove-button" type="button" onClick={() => updateReport(report, "resolved", true)} disabled={Boolean(reportActionId)}><Trash2 size={14} /> {reportActionId === report.id ? "처리 중…" : "메시지 삭제"}</button>}</div>
        </article>)}</div>}
      </section>
      <footer className="admin-updated">{metrics?.generatedAt ? `${formatTime(metrics.generatedAt)} 기준 · 새로고침할 때만 D1을 조회합니다.` : "통계를 불러오는 중입니다…"}</footer>
    </section>
  </main>;
}

function PublicProfileModal({ profile, onClose }) {
  return (
    <Modal title="프로필" onClose={onClose}>
      <section className="public-profile-view">
        <div className="public-profile-visual">
          <ProfileAvatar className="public-profile-avatar" userId={profile.id} avatarUrl={profile.avatarUrl} name={profile.displayName} />
        </div>
        <div className="public-profile-details">
          <div><span>설정 이름</span><strong>{profile.displayName}</strong></div>
          <div><span>실명</span><strong className={profile.realName ? "" : "public-profile-empty"}>{profile.realName || "확인된 실명이 없습니다."}</strong></div>
          <div><span>소개</span><p className={profile.bio ? "" : "public-profile-empty"}>{profile.bio || "아직 작성한 소개가 없습니다."}</p></div>
        </div>
      </section>
    </Modal>
  );
}

function NewConversationModal({ onClose, onCreateGroup, onStartDirect }) {
  const [mode, setMode] = useState("direct");
  const [query, setQuery] = useState("");
  const [name, setName] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const teachers = results.filter((person) => ["teacher", "admin"].includes(person.schoolRole));
  const students = results.filter((person) => person.schoolRole === "student");
  const filteredResults = roleFilter === "teacher" ? teachers : roleFilter === "student" ? students : results;

  async function searchPeople(event) {
    event.preventDefault();
    setError(""); setSearched(false); setLoading(true); setRoleFilter("all");
    try {
      const payload = await apiRequest(`/api/users/search?q=${encodeURIComponent(query.trim())}`);
      setResults(payload.users); setSearched(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }

  return <Modal title="새 메시지" onClose={onClose} wide><div className="conversation-mode-tabs" role="tablist" aria-label="대화 방식"><button type="button" className={mode === "direct" ? "active" : ""} onClick={() => { setMode("direct"); setError(""); }} role="tab" aria-selected={mode === "direct"}>1:1 대화</button><button type="button" className={mode === "group" ? "active" : ""} onClick={() => { setMode("group"); setError(""); }} role="tab" aria-selected={mode === "group"}>그룹 대화</button></div>{mode === "direct" ? <div className="person-search"><form className="person-search-form" onSubmit={searchPeople}><label><span>이름 또는 아이디로 사람 찾기</span><div><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="예: 강용환" maxLength="30" /><button type="submit" disabled={!query.trim() || loading} aria-busy={loading}><Search size={16} /> {loading ? "검색 중…" : "검색"}</button></div></label></form><p className="person-search-hint">남해타임 전체 학생·교직원 명단에서 검색합니다. 아직 남톡에 들어오지 않은 사람에게도 메시지를 미리 보낼 수 있습니다.</p>{error && <p className="form-error">{error}</p>}{searched && <div className="person-result-tools"><div className="person-filters" aria-label="검색 결과 분류"><button type="button" className={roleFilter === "all" ? "active" : ""} onClick={() => setRoleFilter("all")}>전체 {results.length}</button><button type="button" className={roleFilter === "teacher" ? "active" : ""} onClick={() => setRoleFilter("teacher")}>교직원 {teachers.length}</button><button type="button" className={roleFilter === "student" ? "active" : ""} onClick={() => setRoleFilter("student")}>학생 {students.length}</button></div><span>{filteredResults.length}명 표시</span></div>}<div className="person-results">{filteredResults.map((person) => <article className="person-result" key={person.id}><ProfileAvatar className="person-result-avatar" userId={person.id} avatarUrl={person.avatarUrl} name={person.displayName} /><div className="person-result-copy"><strong>{person.displayName}</strong><span>{personMeta(person)}</span></div>{person.isCurrentUser ? <button type="button" disabled>내 계정</button> : <button type="button" disabled={saving} onClick={async () => { setSaving(true); setError(""); try { await onStartDirect(person.id); } catch (requestError) { setError(requestError.message); setSaving(false); } }}>{saving ? "여는 중…" : "대화 시작"}</button>}</article>)}{searched && !filteredResults.length && <p className="person-empty">해당 분류에 검색 결과가 없습니다. 이름이나 학교 아이디를 다시 확인해주세요.</p>}</div></div> : <form className="modal-form" onSubmit={async (event) => { event.preventDefault(); setSaving(true); setError(""); try { await onCreateGroup(name); } catch (requestError) { setError(requestError.message); setSaving(false); } }}><p className="modal-help">여러 명이 함께 이야기할 때 그룹 대화방을 만든 뒤 멤버를 초대하세요.</p><label><span>그룹 대화방 이름</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="예: 1학년 담임 협의" maxLength="50" required /></label>{error && <p className="form-error">{error}</p>}<button className="modal-primary" type="submit" disabled={saving || !name.trim()}>{saving ? "만드는 중…" : "그룹 대화 만들기"}</button></form>}</Modal>;
}

function PushNotificationControl() {
  const supported = window.isSecureContext && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
  const [state, setState] = useState({ checking: true, configured: false, subscribed: false, publicKey: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!supported) {
      setState({ checking: false, configured: false, subscribed: false, publicKey: "" });
      return () => { active = false; };
    }
    (async () => {
      try {
        const [config, registration] = await Promise.all([
          apiRequest("/api/push/config"),
          navigator.serviceWorker.ready,
        ]);
        const subscription = await registration.pushManager.getSubscription();
        if (active) setState({ checking: false, configured: config.enabled, subscribed: Boolean(subscription), publicKey: config.publicKey || "" });
      } catch (requestError) {
        if (active) {
          setState((current) => ({ ...current, checking: false }));
          setError(requestError.message);
        }
      }
    })();
    return () => { active = false; };
  }, [supported]);

  async function enablePush() {
    setSaving(true); setError("");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("브라우저에서 알림 권한을 허용해야 합니다.");
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(state.publicKey),
      });
      try {
        await apiRequest("/api/push/subscriptions", {
          method: "POST",
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      } catch (requestError) {
        await subscription.unsubscribe().catch(() => false);
        throw requestError;
      }
      setState((current) => ({ ...current, subscribed: true }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  async function disablePush() {
    setSaving(true); setError("");
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await apiRequest("/api/push/subscriptions", { method: "DELETE", body: JSON.stringify({ endpoint: subscription.endpoint }) });
        await subscription.unsubscribe();
      }
      setState((current) => ({ ...current, subscribed: false }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  const description = !supported
    ? "이 브라우저에서는 웹 푸시를 사용할 수 없습니다. iPhone·iPad는 홈 화면에 남톡을 추가한 뒤 사용할 수 있습니다."
    : state.checking ? "알림 사용 가능 여부를 확인하고 있습니다…"
      : !state.configured ? "푸시 서버 키가 연결되면 이 기기에서 알림을 켤 수 있습니다."
        : state.subscribed ? "이 기기에서 새 메시지 알림을 받습니다. 메시지 내용은 알림에 표시하지 않습니다."
          : "새 메시지가 오면 내용 없이 도착 여부만 알려드립니다.";

  return <section className="push-setting" aria-labelledby="push-setting-title"><div className="push-setting-copy"><span className="push-setting-icon">{state.subscribed ? <Bell size={18} /> : <BellOff size={18} />}</span><div><strong id="push-setting-title">새 메시지 푸시 알림</strong><p>{description}</p></div></div>{error && <p className="form-error">{error}</p>}{supported && state.configured && !state.checking && <button className={`push-setting-button ${state.subscribed ? "push-setting-button--off" : ""}`} type="button" onClick={state.subscribed ? disablePush : enablePush} disabled={saving}>{saving ? "처리 중…" : state.subscribed ? "이 기기 알림 끄기" : "이 기기 알림 켜기"}</button>}</section>;
}

function PushNotificationGuideModal({ installPrompt, onInstallPromptUsed, onClose }) {
  const userAgent = navigator.userAgent || "";
  const isiOS = /iPad|iPhone|iPod/u.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/u.test(userAgent);
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const [installing, setInstalling] = useState(false);
  const [installMessage, setInstallMessage] = useState("");

  async function installApp() {
    if (!installPrompt || installing) return;
    setInstalling(true);
    setInstallMessage("");
    try {
      const result = await installPrompt.prompt();
      setInstallMessage(result?.outcome === "accepted" ? "설치를 시작했습니다. 설치된 남톡 아이콘으로 열어주세요." : "설치를 취소했습니다. 브라우저 메뉴에서도 다시 설치할 수 있습니다.");
    } finally {
      onInstallPromptUsed();
      setInstalling(false);
    }
  }

  return <Modal title="푸시 알림 켜기" onClose={onClose} wide>
    <div className="push-guide">
      <section className="push-guide-intro">
        <span><Bell size={23} /></span>
        <div><strong>남톡을 닫아도 새 메시지를 확인하세요.</strong><p>알림에는 대화 내용이나 보낸 사람 이름을 표시하지 않습니다.</p></div>
      </section>

      <div className="push-device-tabs" aria-label="기기별 푸시 알림 안내">
        <article className={isiOS ? "push-device-card push-device-card--current" : "push-device-card"}>
          <span className="push-device-icon"><Share2 size={19} /></span>
          <div><strong>iPhone · iPad</strong>{isiOS && <em>현재 기기</em>}</div>
          {standalone && isiOS
            ? <p><CheckCircle2 size={15} /> 홈 화면의 남톡 앱으로 실행 중입니다. 아래에서 알림을 켜세요.</p>
            : <ol><li>브라우저의 <b>공유</b> 버튼을 누릅니다.</li><li><b>홈 화면에 추가</b>를 선택합니다.</li><li>홈 화면의 <b>남톡 아이콘</b>으로 다시 열고 알림을 켭니다.</li></ol>}
        </article>
        <article className={isAndroid ? "push-device-card push-device-card--current" : "push-device-card"}>
          <span className="push-device-icon"><Smartphone size={19} /></span>
          <div><strong>Android</strong>{isAndroid && <em>현재 기기</em>}</div>
          <ol><li>아래 <b>남톡 앱 설치</b> 또는 브라우저 메뉴의 <b>앱 설치</b>를 누릅니다.</li><li>설치된 남톡을 열고 아래에서 알림을 켭니다.</li><li>설치하지 않아도 지원 브라우저에서는 바로 알림을 켤 수 있습니다.</li></ol>
          {installPrompt && <button className="push-install-button" type="button" onClick={installApp} disabled={installing}><Download size={16} /> {installing ? "설치 준비 중…" : "남톡 앱 설치"}</button>}
        </article>
      </div>
      {installMessage && <p className="push-install-message" role="status">{installMessage}</p>}
      <PushNotificationControl />
      <p className="push-guide-note">알림을 차단한 적이 있다면 기기 설정의 알림 또는 브라우저 사이트 설정에서 남톡 권한을 허용해주세요.</p>
    </div>
  </Modal>;
}

function AvatarSetting({ user, onSaved }) {
  const [current, setCurrent] = useState(user.avatarUrl || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  async function choosePreset(preset) {
    if (saving || current === preset) return;
    setSaving(true); setError("");
    try {
      const payload = await apiRequest("/api/auth/avatar", { method: "PATCH", body: JSON.stringify({ preset }) });
      setCurrent(payload.avatarUrl);
      onSaved({ ...user, avatarUrl: payload.avatarUrl });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  async function uploadCustom(file) {
    if (!file) return;
    setError("");
    if (file.size > 1024 * 1024) {
      setError("프로필 사진은 1MB 이하만 올릴 수 있습니다.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("JPG, PNG, WebP 이미지만 사용할 수 있습니다.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.set("avatar", file);
      const payload = await apiRequest("/api/auth/avatar", { method: "POST", body: formData });
      setCurrent(payload.avatarUrl);
      onSaved({ ...user, avatarUrl: payload.avatarUrl });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return <section className="avatar-setting" aria-labelledby="avatar-setting-title"><div className="setting-heading"><div><strong id="avatar-setting-title">프로필 사진</strong><p>기본 아바타를 고르거나 1MB 이하 사진을 올려보세요.</p></div><ProfileAvatar className="avatar-setting-current" userId={user.id} avatarUrl={current} name={user.displayName} /></div><div className="avatar-preset-grid">{avatarOptions.map((option) => <button type="button" key={option.id} className={current === option.id ? "selected" : ""} onClick={() => choosePreset(option.id)} disabled={saving} aria-label={option.label} aria-pressed={current === option.id}><AvatarPreset value={option.id} /><span>{option.label.replace(/^(남학생|여학생)\s/u, "")}</span></button>)}</div><input ref={inputRef} className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => uploadCustom(event.target.files?.[0])} /><button className="avatar-upload-button" type="button" onClick={() => inputRef.current?.click()} disabled={saving}><ImagePlus size={16} /> {saving ? "변경 중…" : "내 사진 올리기"}<small>JPG · PNG · WebP / 최대 1MB</small></button>{error && <p className="form-error">{error}</p>}</section>;
}

function ChatFontSizeSetting({ value, onChange }) {
  return <section className="font-size-setting" aria-labelledby="font-size-title"><div className="setting-heading"><div><strong id="font-size-title">채팅방 글자 크기</strong><p>이 기기에서 보이는 메시지 크기를 조정합니다.</p></div><span className="font-size-sample" style={{ fontSize: `${14 * (chatFontSizes.find((option) => option.id === value)?.scale || 1)}px` }}>가나다</span></div><div className="font-size-options" role="group" aria-label="채팅방 글자 크기 5단계">{chatFontSizes.map((option, index) => <button type="button" key={option.id} className={value === option.id ? "selected" : ""} onClick={() => onChange(option.id)} aria-pressed={value === option.id}><span style={{ fontSize: `${9 + index * 1.5}px` }}>가</span><small>{option.label}</small></button>)}</div></section>;
}

function ProfileModal({ user, chatFontSize, onChatFontSizeChange, onClose, onSaved, onLogout }) {
  const [form, setForm] = useState({ displayName: user.displayName, bio: user.bio || "" });
  const [error, setError] = useState("");
  return <Modal title="내 프로필 설정" onClose={onClose} wide><form className="modal-form profile-settings-form" onSubmit={async (event) => { event.preventDefault(); setError(""); try { const payload = await apiRequest("/api/auth/profile", { method: "PATCH", body: JSON.stringify(form) }); onSaved(payload.user); onClose(); } catch (requestError) { setError(requestError.message); } }}><AvatarSetting user={user} onSaved={onSaved} /><ChatFontSizeSetting value={chatFontSize} onChange={onChatFontSizeChange} /><div className="profile-text-fields"><label><span>표시 이름</span><input value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} maxLength="30" /></label><label><span>소개</span><textarea value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} maxLength="120" placeholder="간단한 소개를 남겨보세요." /></label></div>{error && <p className="form-error">{error}</p>}<button className="modal-primary" type="submit">프로필 정보 저장</button><PushNotificationControl /><button className="modal-danger-outline" type="button" onClick={onLogout}><LogOut size={16} /> 로그아웃</button></form></Modal>;
}

function MembersModal({ room, currentUser, onClose, onChanged }) {
  const [members, setMembers] = useState([]); const [query, setQuery] = useState(""); const [results, setResults] = useState([]); const [searched, setSearched] = useState(false); const [addingId, setAddingId] = useState(""); const [error, setError] = useState("");
  const canAdd = ["owner", "moderator"].includes(room.role) && room.kind === "group";
  const load = useCallback(async () => { const payload = await apiRequest(`/api/rooms/${room.id}/members`); setMembers(payload.members); }, [room.id]);
  useEffect(() => { load().catch((requestError) => setError(requestError.message)); }, [load]);
  const memberIds = new Set(members.map((member) => member.id));
  return <Modal title={`멤버 · ${room.name}`} onClose={onClose} wide><div className="member-list">{members.map((member) => <div className="member-row" key={member.id}><ProfileAvatar className="member-avatar" userId={member.id} avatarUrl={member.avatarUrl} name={member.displayName} /><div><strong>{member.displayName}{member.id === currentUser.id ? " (나)" : ""}</strong><small>{member.identityProvider === "namhae" ? "남해타임 연동" : `@${member.loginId}`}</small></div><em>{member.role === "owner" ? "방장" : member.role === "moderator" ? "관리자" : "멤버"}</em></div>)}</div>{canAdd && <div className="member-add member-directory"><form className="person-search-form" onSubmit={async (event) => { event.preventDefault(); setError(""); setSearched(false); try { const payload = await apiRequest(`/api/users/search?q=${encodeURIComponent(query.trim())}`); setResults(payload.users); setSearched(true); } catch (requestError) { setError(requestError.message); } }}><label><span>이름 또는 아이디로 멤버 찾기</span><div><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="추가할 사람 검색" maxLength="30" /><button type="submit" disabled={!query.trim()}><Search size={16} /> 검색</button></div></label></form><p className="person-search-hint">남해타임 전체 학생·교직원 명단에서 검색합니다.</p><div className="person-results">{results.map((person) => <article className="person-result" key={person.id}><ProfileAvatar className="person-result-avatar" userId={person.id} avatarUrl={person.avatarUrl} name={person.displayName} /><div className="person-result-copy"><strong>{person.displayName}</strong><span>{person.identityProvider === "namhae" ? "남해타임 연동" : `@${person.loginId}`}</span></div>{memberIds.has(person.id) ? <em className="person-added">참여 중</em> : <button type="button" disabled={Boolean(addingId)} onClick={async () => { setAddingId(person.id); setError(""); try { await apiRequest(`/api/rooms/${room.id}/members`, { method: "POST", body: JSON.stringify({ userId: person.id }) }); await load(); await onChanged(); } catch (requestError) { setError(requestError.message); } finally { setAddingId(""); } }}><UserPlus size={15} /> 추가</button>}</article>)}{searched && !results.length && <p className="person-empty">검색 결과가 없습니다.</p>}</div></div>}{error && <p className="form-error modal-error">{error}</p>}</Modal>;
}

function RoomSettingsModal({ room, onClose, onChanged }) {
  const [name, setName] = useState(room.name); const [error, setError] = useState("");
  return <Modal title="대화방 설정" onClose={onClose}><form className="modal-form" onSubmit={async (event) => { event.preventDefault(); setError(""); try { await apiRequest(`/api/rooms/${room.id}/settings`, { method: "PATCH", body: JSON.stringify({ name }) }); await onChanged(); } catch (requestError) { setError(requestError.message); } }}>{room.kind === "group" ? <><label><span>대화방 이름</span><input value={name} onChange={(event) => setName(event.target.value)} disabled={room.role !== "owner"} maxLength="50" /></label>{error && <p className="form-error">{error}</p>}{room.role === "owner" && <><button className="modal-primary" type="submit">이름 저장</button><button className="modal-danger-outline" type="button" onClick={async () => { if (!window.confirm("이 대화방을 보관할까요? 목록에서 숨겨집니다.")) return; await apiRequest(`/api/rooms/${room.id}/settings`, { method: "PATCH", body: JSON.stringify({ archived: true }) }); await onChanged(); }}><Archive size={16} /> 대화방 보관</button></>}</> : <p className="modal-help">{room.kind === "direct" ? "1:1 대화의 이름은 상대방의 표시 이름으로 자동 관리됩니다." : "나에게 보내기 방은 계정과 함께 유지됩니다."}</p>}</form></Modal>;
}

export default App;
